/**
 * Theme Provider and extendTheme
 * Main theme system for the component library
 */

import React, { createContext, useContext, useMemo } from 'react';
import { ThemeProvider as EmotionThemeProvider } from '@emotion/react';
import { ColorModeProvider, useColorMode, mode } from '../system/color-mode';

// Token imports
import colors from './tokens/colors';
import breakpoints from './tokens/breakpoints';
import { spacing, sizes, radii, borders, shadows, zIndices } from './tokens/spacing';
import { fonts, fontSizes, fontWeights, lineHeights, letterSpacings } from './tokens/typography';

// Component style imports
import buttonStyles from './components/button';
import { inputStyles, numberInputStyles, selectStyles } from './components/input';
import badgeStyles from './components/badge';
import switchStyles from './components/switch';
import textareaStyles from './components/textarea';
import cardStyles from './components/card';
import linkStyles from './components/link';
import progressStyles from './components/progress';
import sliderStyles from './components/slider';
import modalStyles from './components/modal';

/**
 * Default theme configuration
 */
const defaultTheme = {
  // Color mode config
  config: {
    initialColorMode: 'light',
    useSystemColorMode: false,
  },

  // Tokens
  colors,
  breakpoints,
  space: spacing,
  sizes,
  radii,
  borders,
  shadows,
  zIndices,
  fonts,
  fontSizes,
  fontWeights,
  lineHeights,
  letterSpacings,

  // Transition defaults
  transition: {
    property: {
      common: 'background-color, border-color, color, fill, stroke, opacity, box-shadow, transform',
      colors: 'background-color, border-color, color, fill, stroke',
      dimensions: 'width, height',
      position: 'left, right, top, bottom',
      background: 'background-color, background-image, background-position',
    },
    easing: {
      'ease-in': 'cubic-bezier(0.4, 0, 1, 1)',
      'ease-out': 'cubic-bezier(0, 0, 0.2, 1)',
      'ease-in-out': 'cubic-bezier(0.4, 0, 0.2, 1)',
    },
    duration: {
      'ultra-fast': '50ms',
      faster: '100ms',
      fast: '150ms',
      normal: '200ms',
      slow: '300ms',
      slower: '400ms',
      'ultra-slow': '500ms',
    },
  },

  // Blur
  blur: {
    none: '0',
    sm: '4px',
    base: '8px',
    md: '12px',
    lg: '16px',
    xl: '24px',
    '2xl': '40px',
    '3xl': '64px',
  },

  // Component styles
  components: {
    Button: buttonStyles,
    Input: inputStyles,
    NumberInput: numberInputStyles,
    Select: selectStyles,
    Badge: badgeStyles,
    Switch: switchStyles,
    Textarea: textareaStyles,
    Card: cardStyles,
    Link: linkStyles,
    Progress: progressStyles,
    Slider: sliderStyles,
    RangeSlider: sliderStyles,
    Modal: modalStyles,
  },

  // Global styles
  styles: {
    global: (props) => ({
      body: {
        overflowX: 'hidden',
        bg: mode('secondaryGray.300', 'navy.900')(props),
        fontFamily: 'DM Sans',
        letterSpacing: '-0.5px',
      },
      '*': {
        boxSizing: 'border-box',
      },
      input: {
        color: 'gray.700',
      },
      html: {
        fontFamily: 'DM Sans',
      },
      '*::placeholder': {
        color: mode('gray.400', 'whiteAlpha.400')(props),
      },
      '*, *::before, &::after': {
        borderColor: mode('gray.200', 'whiteAlpha.300')(props),
      },
    }),
  },
};

/**
 * Deep merge utility for themes
 */
const deepMerge = (target, source) => {
  const output = { ...target };
  
  if (isObject(target) && isObject(source)) {
    Object.keys(source).forEach(key => {
      if (isObject(source[key])) {
        if (!(key in target)) {
          Object.assign(output, { [key]: source[key] });
        } else {
          output[key] = deepMerge(target[key], source[key]);
        }
      } else {
        Object.assign(output, { [key]: source[key] });
      }
    });
  }
  
  return output;
};

const isObject = (item) => {
  return item && typeof item === 'object' && !Array.isArray(item);
};

/**
 * Extend theme with custom values
 * API compatible with Chakra's extendTheme
 * 
 * @param {...object} overrides - Theme override objects
 * @returns {object} - Merged theme
 */
export const extendTheme = (...overrides) => {
  return overrides.reduce((acc, override) => {
    return deepMerge(acc, override);
  }, defaultTheme);
};

/**
 * Theme Context
 */
const ThemeContext = createContext(defaultTheme);

/**
 * useTheme Hook
 * Returns the current theme object
 */
export const useTheme = () => {
  const theme = useContext(ThemeContext);
  return theme;
};

/**
 * ChakraProvider - Main provider component
 * Wraps the app with theme and color mode context
 */
export const ChakraProvider = ({ 
  theme = defaultTheme, 
  children,
  resetCSS = true,
  portalZIndex,
}) => {
  const mergedTheme = useMemo(() => {
    return theme === defaultTheme ? defaultTheme : deepMerge(defaultTheme, theme);
  }, [theme]);

  return (
    <ThemeContext.Provider value={mergedTheme}>
      <ColorModeProvider 
        initialColorMode={mergedTheme.config?.initialColorMode || 'light'}
        useSystemColorMode={mergedTheme.config?.useSystemColorMode || false}
      >
        <EmotionThemeProvider theme={mergedTheme}>
          {resetCSS && <CSSReset />}
          {children}
        </EmotionThemeProvider>
      </ColorModeProvider>
    </ThemeContext.Provider>
  );
};

/**
 * CSS Reset component
 * Applies global reset styles
 */
const CSSReset = () => {
  return (
    <style>{`
      *, *::before, *::after {
        box-sizing: border-box;
        margin: 0;
        padding: 0;
        border: 0 solid;
      }
      html {
        line-height: 1.5;
        -webkit-text-size-adjust: 100%;
        font-family: system-ui, sans-serif;
        -webkit-font-smoothing: antialiased;
        text-rendering: optimizeLegibility;
      }
      body {
        min-height: 100vh;
      }
      a {
        color: inherit;
        text-decoration: inherit;
      }
      button, input, optgroup, select, textarea {
        font-family: inherit;
        font-size: 100%;
        line-height: 1.15;
        margin: 0;
      }
      button, select {
        text-transform: none;
      }
      button, [type="button"], [type="reset"], [type="submit"] {
        -webkit-appearance: button;
      }
      table {
        border-collapse: collapse;
        border-spacing: 0;
      }
      img, video {
        max-width: 100%;
        height: auto;
      }
      [hidden] {
        display: none;
      }
    `}</style>
  );
};

/**
 * withTheme HOC
 * Wraps a component and injects theme prop
 */
export const withTheme = (Component) => {
  const WithTheme = (props) => {
    const theme = useTheme();
    return <Component {...props} theme={theme} />;
  };
  
  WithTheme.displayName = `WithTheme(${Component.displayName || Component.name || 'Component'})`;
  return WithTheme;
};

export { defaultTheme, ThemeContext };
